import Icon from "@components/ui/icon";
import Input from "@components/ui/input";
import Spinner from "@components/ui/spinner";
import { cn } from "@lib/cn";
import debounce from "lodash.debounce";
import { type KeyboardEvent, useCallback, useRef, useState } from "react";
import { useOnClickOutside } from "usehooks-ts";
import { track } from "@/features/analytics";
import type { IBrandfetchBrand } from "@/shared/types";
import { useLogosStoreShallow } from "../store";

interface IProps {
  className?: string;
}

const API_URL = "https://api.brandfetch.io/v2/search";

export const LogoFinderSearchInput = ({ className }: IProps) => {
  const { query, setQuery, setBrand, setLogosData } = useLogosStoreShallow(
    (s) => ({
      setLogosData: s.setLogosData,
      query: s.query,
      setQuery: s.setQuery,
      setBrand: s.setBrand,
    }),
  );
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [selectedSearch, setSelectedSearch] = useState(-1);
  const [isFetching, setIsFetching] = useState(false);
  const [brands, setBrands] = useState<IBrandfetchBrand[]>([]);

  useOnClickOutside(containerRef, () => {
    setIsFocused(false);
  });

  const refetchData = useCallback(
    debounce(async (query: string) => {
      try {
        setIsFetching(true);
        const resp = await fetch(`${API_URL}/${query}`);
        const data = (await resp.json()) as IBrandfetchBrand[];
        setBrands(data);
      } catch (e) {
        console.log(e);
        track("error fetching brands from brandfetch", {
          error: e,
          search: query,
        });
      }
      setIsFetching(false);
    }, 500),
    [],
  );

  const handleSubmit = (brand: IBrandfetchBrand) => {
    if (!inputRef.current) return;
    setQuery(brand.domain);
    setBrand(brand);
    inputRef.current.blur();
    setIsFocused(false);
  };

  const onClear = () => {
    setBrand(null);
    setQuery("");
    setLogosData(
      Array.from({ length: 14 }, (_, idx) => {
        const src = `${window.location.origin}/assets/images/brand-logos/${idx}.png`;
        return {
          type: "logo",
          color: "color",
          src,
          originalSrc: src,
        };
      }),
    );
  };

  const handleClear = () => {
    setBrands([]);
    onClear();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      e.currentTarget.blur();
      setIsFocused(false);
      return;
    }

    const isArrowDown = e.key === "ArrowDown";
    const isArrowUp = e.key === "ArrowUp";
    if (isArrowUp || isArrowDown) {
      e.preventDefault();
      setSelectedSearch((prev) => {
        const newVal = prev + (isArrowDown ? 1 : -1);

        if (newVal > brands.length - 1) {
          return 0;
        }
        if (newVal < 0) {
          return brands.length - 1;
        }

        return newVal;
      });
      return;
    }

    if (e.key === "Enter") {
      e.currentTarget.blur();
      if (brands.length === 0) {
        handleClear();
        return;
      }

      const brand = selectedSearch < 0 ? brands[0] : brands[selectedSearch];
      if (brand) {
        handleSubmit(brand);
      }
    }
  };

  return (
    <div className={cn("tw-relative", className)} ref={containerRef}>
      <Input
        type="text"
        ref={inputRef}
        autoFocus={false}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        placeholder="Enter a brand name or website URL"
        icon="ri-search-line"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setSelectedSearch(-1);

          const query = e.target.value.trim();
          if (query) {
            setIsFetching(true);
            refetchData(query);
          }
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          if (query.length === 0) {
            handleClear();
          }
        }}
      />
      {isFetching ? (
        <div className="tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2">
          <Spinner />
        </div>
      ) : (
        onClear &&
        query.length > 0 &&
        !isFocused && (
          <button
            type="button"
            className="tw-absolute tw-right-3 tw-top-1/2 tw-grid -tw-translate-y-1/2 tw-items-center tw-outline-none"
            onClick={() => onClear()}
          >
            <Icon
              icon="ri-close-line"
              className="tw-text-[24px]/none tw-text-blue-500"
            />
          </button>
        )
      )}

      {isFocused && brands.length > 0 && (
        <div className="tw-absolute tw-left-0 tw-top-full tw-z-10 tw-mt-2 tw-w-full tw-rounded-lg tw-border tw-border-blue-100 tw-bg-white tw-pt-2 tw-shadow-popover">
          <ol className="tw-m-0 tw-grid tw-list-none tw-content-start tw-gap-1 tw-px-2">
            {brands.map((brand, idx) => (
              <li
                key={brand.brandId}
                role="option"
                tabIndex={0}
                aria-selected={selectedSearch === idx}
                onClick={() => handleSubmit(brand)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleSubmit(brand); }}
                className={cn({
                  "group/item tw-flex tw-cursor-pointer tw-items-center tw-rounded-md tw-px-3 tw-py-2 hover:tw-bg-blue-50": true,
                  "tw-bg-blue-50": selectedSearch === idx,
                })}
              >
                <div className="tw-mr-2 tw-size-5">
                  <img
                    alt="Brand logo"
                    src={brand.icon}
                    loading="lazy"
                    className="tw-size-full tw-rounded-full tw-bg-blue-50"
                  />
                </div>
                <span className="tw-mr-auto tw-text-[11px] group-hover:tw-text-primary">
                  {brand.name}
                </span>
                <span className="tw-text-[11px] tw-text-blue-500">
                  {brand.domain}
                </span>
              </li>
            ))}
          </ol>
          <div className="tw-border-t tw-border-blue-100 tw-py-2 tw-text-center tw-text-[10px] tw-font-semibold tw-uppercase tw-text-blue-500">
            Provided by Brandfetch
          </div>
        </div>
      )}
    </div>
  );
};
